import { FC, useRef } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/icons-material';
import { Box, InputAdornment, Typography } from '@mui/material';

import FormRichEditor from '@components/FormRichEditor/FormRichEditor';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import ImageSlickCarousel from '@components/ImageMultiCarousel/ImageMultiCarousel';
import ViewProductPageButton from '@components/ViewProductPageButton/ViewProductPageButton';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import CannedResponses from '../../../CannedResponses/CannedResponses';
import { getParsedText } from '../../SupplementInformationForm/utils';
import { appendComment } from '../../utils';
import { TNewSupplementResponseMode } from '../NewSupplement.interface';

import { INewSupplementFormProps } from './NewSupplementForm.interface';
import { isOnlyCommentsMode } from './utils';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
];

const NewSupplementForm: FC<INewSupplementFormProps> = ({
  form,
  images,
  mode,
  handleSubmit,
  onImageClick,
  values,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLInputElement | null>(null);

  const supplementCategories = useSupplementCategories();
  const onlyCommentsMode = isOnlyCommentsMode(mode);

  const commentsFieldName: string =
    (mode === TNewSupplementResponseMode.NeedMoreInfo &&
      t('adminNewSupplement.needMoreInfoComments')) ||
    (mode === TNewSupplementResponseMode.MarkAsMedication &&
      t('adminNewSupplement.medicationComments')) ||
    t('adminNewSupplement.comments');

  return (
    <form
      className="new-supplement-form"
      id="admin-new-supplement-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <Typography variant="h3" className="new-supplement-form__title">
        {t('inquiryResponse.needsInfoTitle')}
      </Typography>

      <Field
        component={FormTextField}
        className="new-supplement-form__input"
        label={t('addSupplementModal.supplementName')}
        name="supplementName"
        disabled={onlyCommentsMode}
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required={!onlyCommentsMode}
      />

      <Field
        component={FormTextField}
        className="new-supplement-form__input"
        label={t('addSupplementModal.companyName')}
        name="companyName"
        disabled={onlyCommentsMode}
        format={removeWhiteSpaces}
        formatOnBlur
        required={!onlyCommentsMode}
      />

      <Field
        component={FormSelect}
        className="new-supplement-form__input"
        label={t('addSupplementModal.productCategory')}
        name="supplementCategoryId"
        options={supplementCategories}
        disabled={onlyCommentsMode}
      />

      <Box className="new-supplement-form__column">
        <Field
          component={FormTextField}
          className="new-supplement-form__input new-supplement-form__column_75 new-supplement-form__link"
          label={t('addSupplementModal.linkToProductPage')}
          name="linkToProductPage"
          InputProps={{
            startAdornment: (
              <InputAdornment className="new-supplement-form__input-icon" position="start">
                <Link color="inherit" />
              </InputAdornment>
            ),
          }}
          disabled={onlyCommentsMode}
          format={removeWhiteSpaces}
          formatOnBlur
          required={!onlyCommentsMode}
        />

        <ViewProductPageButton
          linkToProductPage={values.linkToProductPage}
          className="new-supplement-form__btn new-supplement-form__column_25"
        />
      </Box>

      {Boolean(Array.isArray(images) && images.length) && (
        <Box className="new-supplement-form__images-section">
          <Typography variant="h3" className="new-supplement-form__title">
            {t('common.photos')}
          </Typography>
          <Box className="new-supplement-form__images-holder" ref={containerRef}>
            <ImageSlickCarousel
              slidesToShow={3}
              containerRef={containerRef}
              responsive={uploadImagesSliderResponsiveSettings}
            >
              {images?.map(image => (
                <Box
                  className="new-supplement-form__slide-holder"
                  onClick={() => onImageClick(image)}
                >
                  <Box width={100} height={100} key={image.url} component="img" src={image.url} />
                </Box>
              ))}
            </ImageSlickCarousel>
          </Box>
        </Box>
      )}

      {mode !== TNewSupplementResponseMode.MatchExisting && (
        <>
          <Typography variant="h3" className="new-supplement-form__title">
            {commentsFieldName}

            <CannedResponses
              isMedication={false}
              onApply={text => {
                form.change('comments', appendComment(getParsedText(text), values.comments));
              }}
            />
          </Typography>

          <Field
            component={FormRichEditor}
            className="new-supplement-form__input new-supplement-form__input_comments"
            label={t('adminNewSupplement.comments')}
            name="comments"
            required={onlyCommentsMode}
          />
        </>
      )}
    </form>
  );
};

export default NewSupplementForm;
